import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CustomerNav } from '../components/CustomerNav';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { Clock, ArrowRight, Sparkles, Utensils, SprayCan, Code } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  spa: Sparkles,
  kitchen: Utensils,
  cleaning: SprayCan,
  coding: Code
};

const categoryColors = {
  spa: 'bg-terracotta/10 text-terracotta border-terracotta/20',
  kitchen: 'bg-sage/10 text-sage border-sage/20',
  cleaning: 'bg-golden-hour/20 text-deep-charcoal border-golden-hour/30',
  coding: 'bg-deep-charcoal/10 text-deep-charcoal border-deep-charcoal/20'
};

export default function Services() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get('category') || 'all';
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    fetchServices();
  }, [activeCategory]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const url = activeCategory === 'all' 
        ? `${API}/services` 
        : `${API}/services?category=${activeCategory}`;
      const response = await axios.get(url);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      spa: t('ftasmSpa'),
      kitchen: t('ftasmKitchen'),
      cleaning: t('ftasmCleaning'),
      coding: t('ftasmCoding')
    };
    return labels[category] || category;
  };

  const categories = [
    { id: 'all', label: t('allServices') },
    { id: 'spa', label: t('spa') },
    { id: 'kitchen', label: t('kitchen') },
    { id: 'cleaning', label: t('cleaning') },
    { id: 'coding', label: t('coding') }
  ];

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <CustomerNav />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-deep-charcoal mb-4">
              {t('ourServices')}
            </h1>
            <p className="font-body text-lg text-deep-charcoal/60 max-w-2xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSearchParams(cat.id === 'all' ? {} : { category: cat.id })}
                data-testid={`filter-${cat.id}`}
                className={`px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.id || (cat.id === 'all' && !searchParams.get('category'))
                    ? 'bg-terracotta text-white shadow-lg'
                    : 'bg-white text-deep-charcoal hover:bg-terracotta/10 border border-deep-charcoal/10'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Services Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = categoryIcons[service.category] || Sparkles;
                return (
                  <div
                    key={service.service_id}
                    data-testid={`service-item-${service.service_id}`}
                    className={`group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-500 hover:-translate-y-1 animate-fade-in-up`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={service.image_url || 'https://images.unsplash.com/photo-1691662754730-2415e37e1052'}
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className={`absolute top-4 ${isRTL ? 'right-4' : 'left-4'}`}>
                        <Badge className={`${categoryColors[service.category]} border`}>
                          <Icon className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                          {getCategoryLabel(service.category)}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-semibold text-deep-charcoal mb-2">
                        {service.name}
                      </h3>
                      <p className="font-body text-sm text-deep-charcoal/60 mb-4 line-clamp-2">
                        {service.description}
                      </p>

                      {/* Meta */}
                      <div className={`flex items-center justify-between mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Clock className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                            <span className="font-body text-sm">{service.duration_minutes} {t('min')}</span>
                          </div>
                          <div className="flex items-center text-terracotta font-semibold">
                            <span className="font-body">{formatPrice(service.price)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Book Button */}
                      <Link to={`/booking/${service.service_id}`}>
                        <Button 
                          className="w-full rounded-xl bg-terracotta text-white hover:bg-terracotta-dark group-hover:shadow-lg transition-all duration-300"
                          data-testid={`book-service-${service.service_id}`}
                        >
                          {t('bookNow')}
                          <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && services.length === 0 && (
            <div className="text-center py-20">
              <p className="font-body text-lg text-deep-charcoal/60">
                No services found in this category.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
